import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Fade from "@mui/material/Fade";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";

export function MobileViewCollectionModal({
  open,
  onClose,
  collection,
}) {
  if (!collection) return null;

  const items = collection.dataIds || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Fade}
      transitionDuration={{
        enter: 350,
        exit: 250,
      }}
      PaperProps={{
        sx: {
          background:
            "linear-gradient(180deg, #fff 0%, #f9f4f6 100%)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Top Glow */}
        <Box
          sx={{
            position: "absolute",
            top: -120,
            right: -100,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(90,28,47,0.18) 0%, transparent 70%)",
            zIndex: 0,
          }}
        />

        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 4,
            pb: 3,
            background:
              "linear-gradient(135deg, rgb(90,28,47) 0%, rgb(130,45,72) 100%)",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
            borderBottomLeftRadius: "28px",
            borderBottomRightRadius: "28px",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -40,
              right: -30,
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              position: "relative",
              zIndex: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(10px)",
                    borderRadius:"8px !important"
                  }}
                >
                  <Inventory2RoundedIcon />
                </Avatar>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 17,
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {collection.name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      opacity: 0.75,
                      mt: 0.5,
                    }}
                  >
                    Created{" "}
                    {new Date(
                      collection.createdAt
                    ).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={`${items.length} Identifier`}
                size="small"
                sx={{
                  background: "rgba(255,255,255,0.14)",
                  color: "#fff",
                  fontWeight: 700,
                  border: "1px solid rgba(255,255,255,0.15)",
                  backdropFilter: "blur(10px)",
                }}
              />
            </Box>

            <IconButton
              onClick={onClose}
              sx={{
                width: 42,
                height: 42,
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  background: "rgba(255,255,255,0.18)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {items.map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "24px",
                  p: 2.2,
                  background:
                    "linear-gradient(180deg, #fff 0%, #fff8fa 100%)",
                  border: "1px solid rgba(90,28,47,0.08)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                  transition: "all 0.28s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow:
                      "0 14px 30px rgba(90,28,47,0.12)",
                  },
                }}
              >
                {/* Card Glow */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -30,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(90,28,47,0.08) 0%, transparent 70%)",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "rgb(90,28,47)",
                    letterSpacing: "1.2px",
                    mb: 1,
                  }}
                >
                IDENTIFIER 
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#222",
                    lineHeight: 1.5,
                    wordBreak: "break-word",
                  }}
                >
                  {item.identifier || "—"}
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Chip
                    label={`#${index + 1}`}
                    size="small"
                    sx={{
                      height: 26,
                      fontWeight: 700,
                      background: "rgba(90,28,47,0.08)",
                      color: "rgb(90,28,47)",
                    }}
                  />

                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "rgb(90,28,47)",
                      boxShadow:
                        "0 0 12px rgba(90,28,47,0.45)",
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>

          {!items.length && (
            <Box
              sx={{
                height: "60vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                textAlign: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  mb: 2,
                  background: "rgba(90,28,47,0.08)",
                  color: "rgb(90,28,47)",
                }}
              >
                <Inventory2RoundedIcon sx={{ fontSize: 34 }} />
              </Avatar>

              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#222",
                }}
              >
                No Identifiers Found
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: "#777",
                  mt: 1,
                }}
              >
                This collection does not contain any items.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}